import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import BlockedReasonTip from '../../../../components/v2/BlockedReasonTip.jsx';
import DeviceNameLink from '../../../../components/v2/DeviceNameLink.jsx';
import { DEVICE_ALARM_BLOCKED_MSG } from '../../../../utils/activeAlerts.js';
import '../../../../components/v2/drawer-tabs.css';
import Drawer from '../../../../components/v2/Drawer.jsx';


/**
 * DeviceDrawer — slide-out drawer for managing device assignments on a resident.
 * Shows currently assigned devices (with remove) and an add interface.
 *
 * Available tab features:
 *   - Filters out area-level devices (is_area_device)
 *   - Sorts unassigned devices first, then devices assigned to other residents
 *   - Shows who a device is currently assigned to
 *   - Requires confirmation before reassigning from another resident
 *   - Shows success/error toast after assign/remove
 *   - Auto-switches to Assigned tab after a successful assignment
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   resident: object | null,
 *   assignedDevices: Array<{ device_id: number, device_name: string, manufacture_uuid: string }>,
 *   availableDevices: Array<{ device_id: number, device_name: string, manufacture_uuid: string, inovonics_model_name: string, resident_id: number, resident_name: string, is_area_device: boolean }>,
 *   onAssignDevice: (residentId: number, deviceId: number) => Promise<[boolean, string?]>,
 *   onRemoveDevice: (residentId: number, deviceId: number) => Promise<[boolean, string?]>,
 *   alertedDeviceIds: Set<string> — device ids with an active alarm, campus-wide
 * }} props
 */
const DeviceDrawer = ({ isOpen, onClose, resident, assignedDevices, availableDevices, onAssignDevice, onRemoveDevice, onAddNewDevice, alertedDeviceIds }) => {
  /* Unassigning or reassigning a device mid-alarm can stop its clear signal being
     processed. Campus-wide set, and ids compare as strings — the subscription
     delivers them as a number on one path and a string on the other. */
  const isAlarming = useCallback(
    (d) => Boolean(alertedDeviceIds?.has(String(d?.device_id))),
    [alertedDeviceIds]
  );
  const ALARM_BLOCKED_MSG = DEVICE_ALARM_BLOCKED_MSG;
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('assigned');
  const [confirmDevice, setConfirmDevice] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const [busy, setBusy] = useState(false);   // loading state during API calls
  const toastTimer = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTab('assigned');
      setConfirmDevice(null);
      setToast(null);
      setBusy(false);
    }
  }, [isOpen]);

  // Auto-clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      toastTimer.current = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(toastTimer.current);
    }
  }, [toast]);

  // Escape cancels a pending assign confirm first; the Drawer shell closes the drawer
  useEffect(() => {
    if (!isOpen || !confirmDevice) return undefined;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setConfirmDevice(null);
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, confirmDevice]);

  /**
   * Filtered & sorted available devices:
   * 1. Exclude area-level devices (is_area_device === true)
   * 2. Exclude devices already assigned to THIS resident
   * 3. Apply search filter
   * 4. Sort: unassigned first, then assigned-to-others
   */
  const filteredAvailable = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return availableDevices
      .filter((d) => {
        if (d.is_area_device === true || d.is_area_device === 1) return false;
        if (assignedDevices.some((ad) => ad.device_id === d.device_id)) return false;
        if (term) {
          return (
            d.device_name.toLowerCase().includes(term) ||
            d.manufacture_uuid.toLowerCase().includes(term) ||
            (d.resident_name || '').toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const aAssigned = a.resident_id && a.resident_id > 0 ? 1 : 0;
        const bAssigned = b.resident_id && b.resident_id > 0 ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });
  }, [availableDevices, assignedDevices, searchTerm]);

  const handleAssign = useCallback(
    async (device) => {
      // If device is assigned to another resident, require confirmation first
      if (device.resident_id && device.resident_id > 0) {
        setConfirmDevice(device);
        return;
      }
      if (!resident) return;

      setBusy(true);
      const result = await onAssignDevice(resident.resident_id, device.device_id);
      setBusy(false);

      if (result[0]) {
        setToast({ type: 'success', message: `${device.device_name} assigned successfully` });
        setTab('assigned');
      } else {
        setToast({ type: 'error', message: result[1] || 'Failed to assign device' });
      }
    },
    [resident, onAssignDevice]
  );

  const handleConfirmReassign = useCallback(async () => {
    if (!resident || !confirmDevice) return;

    const deviceName = confirmDevice.device_name;
    setBusy(true);
    setConfirmDevice(null);
    const result = await onAssignDevice(resident.resident_id, confirmDevice.device_id);
    setBusy(false);

    if (result[0]) {
      setToast({ type: 'success', message: `${deviceName} reassigned successfully` });
      setTab('assigned');
    } else {
      setToast({ type: 'error', message: result[1] || 'Failed to reassign device' });
    }
  }, [resident, confirmDevice, onAssignDevice]);

  const handleCancelReassign = useCallback(() => {
    setConfirmDevice(null);
  }, []);

  const handleRemove = useCallback(
    async (device) => {
      if (!resident) return;

      setBusy(true);
      const result = await onRemoveDevice(resident.resident_id, device.device_id, device.device_name, device?.manufacture_id);
      setBusy(false);

      if (result[0]) {
        setToast({ type: 'success', message: `${device.device_name} removed` });
      } else {
        setToast({ type: 'error', message: result[1] || 'Failed to remove device' });
      }
    },
    [resident, onRemoveDevice]
  );

  // The drawer wrapper is rendered unconditionally (even when `resident` is
  // null) so the first open has a closed → open transition to animate.
  // Previously an early-return on `!resident` meant the drawer mounted for
  // the first time already in the open state, skipping the slide-in
  // animation. Inner content that references `resident.*` is guarded below.

  return (
    <Drawer
      open={!!(isOpen && resident)}
      onClose={onClose}
      classPrefix="rp"
      size="md"
      ariaLabel="Manage Devices"
      headerStart={(
        <div>
          <h2 className="rp-drawer__title">Manage Devices</h2>
          <p className="rp-drawer__subtitle">
            {resident?.resident_first_name} {resident?.resident_last_name}
          </p>
        </div>
      )}
      rawBody
    >
      {resident ? (
      <>
      {/* Toast notification */}
      {toast ? (
        <div
          className={`rp-drawer-toast ${toast.type === 'success' ? 'rp-drawer-toast--success' : 'rp-drawer-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="rp-drawer-toast__close"
            aria-label="Dismiss"
          >
            <svg className="rp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : <></>}

      {/* Tab bar */}
      <div className="dp-drawer-tabs">
        <button
          type="button"
          onClick={() => { setTab('assigned'); setConfirmDevice(null); }}
          className={`dp-drawer-tab ${tab === 'assigned' ? 'is-active' : ''}`}
        >
          Assigned ({assignedDevices.length})
        </button>
        <button
          type="button"
          onClick={() => { setTab('available'); setConfirmDevice(null); }}
          data-help-id="device-drawer.available-tab"
          className={`dp-drawer-tab ${tab === 'available' ? 'is-active' : ''}`}
        >
          Available ({filteredAvailable.length})
        </button>
      </div>

      {/* Loading overlay */}
      {busy ? (
        <div className="rp-drawer-busy" aria-live="polite">
          <svg className="rp-icon-md rp-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : <></>}

      {/* Content */}
      <div className="rp-drawer__body">
        {tab === 'assigned' ? (
          <div className="rp-drawer__body-padded">
            {assignedDevices.length === 0 ? (
              <div className="rp-drawer-empty">
                <svg className="rp-icon-xl rp-drawer-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
                </svg>
                <p className="rp-drawer-empty__text">No devices assigned</p>
                <button
                  type="button"
                  onClick={() => setTab('available')}
                  className="rp-drawer-empty__link"
                >
                  Assign a device
                </button>
              </div>
            ) : (
              <div className="rp-drawer-items">
                {assignedDevices.map((device) => {
                  const alarming = isAlarming(device);
                  return (
                  <div key={device.device_id} className="rp-drawer-item">
                    <div>
                      <p className="rp-drawer-item__name"><DeviceNameLink device={device} className="rp-drawer-item__link" /></p>
                      <p className="rp-drawer-item__meta ms-2">
                        {device.inovonics_model_name ? <span>{device.inovonics_model_name}</span> : null}
                        {device.inovonics_model_name && device.manufacture_uuid ? ' · ' : ''}
                        {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : null}
                      </p>
                      {device.descriptions ? (
                        <p className="rp-drawer-item__meta ms-2">{device.descriptions}</p>
                      ) : null}
                      {alarming ? (
                        <p className="rp-drawer-item__alarm">
                          <span className="rp-drawer-item__alarm-dot" aria-hidden="true" />
                          In alarm
                        </p>
                      ) : null}
                    </div>
                    <BlockedReasonTip message={alarming ? ALARM_BLOCKED_MSG : ''}>
                      <button
                        type="button"
                        onClick={() => handleRemove(device)}
                        className="rp-drawer-btn--remove"
                        disabled={busy || alarming}
                        aria-label={`Remove ${device.device_name}`}
                      >
                        <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Remove
                      </button>
                    </BlockedReasonTip>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Sticky area: search + reassign confirmation */}
            <div className="rp-drawer-sticky">
              {/* Search available devices */}
              <div className="rp-drawer-search">
                <svg className="rp-icon-md rp-drawer-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search devices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rp-drawer-search__input"
                />
              </div>

              {/* Reassign confirmation banner */}
              {confirmDevice ? (
                <div className="rp-device-confirm" role="alert">
                  <div className="rp-device-confirm__message">
                    <svg className="rp-icon-md rp-device-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="rp-device-confirm__title">
                        Reassign device?
                      </p>
                      <p className="rp-device-confirm__detail">
                        <strong>{confirmDevice.device_name}</strong> is currently assigned to{' '}
                        <strong>{confirmDevice.resident_name}</strong>. Reassign it to{' '}
                        <strong>{resident.resident_first_name} {resident.resident_last_name}</strong>?
                      </p>
                    </div>
                  </div>
                  <div className="rp-device-confirm__actions">
                    <button
                      type="button"
                      onClick={handleCancelReassign}
                      className="rp-btn rp-btn--cancel rp-btn--sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmReassign}
                      className="rp-btn rp-btn--warning rp-btn--sm"
                    >
                      Reassign
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Scrollable device list */}
            <div className="rp-drawer__body-padded" style={{ flex: 1, overflowY: 'auto' }}>
              {/* Persistent so a brand-new device can be added without having
                  to search and fail first.

                  Carries the help anchor; the twin button in the empty state
                  below deliberately does NOT. Both render at once when the
                  available list is empty, and two elements sharing one
                  data-help-id would make the spotlight target ambiguous. This
                  one always renders on the Available tab, so it's the reliable
                  target — don't copy the attribute onto the other. */}
              {onAddNewDevice ? (
                <button
                  type="button"
                  onClick={onAddNewDevice}
                  data-help-id="device-drawer.add-new"
                  className="rp-btn rp-btn--primary"
                  style={{ width: '100%', marginBottom: 'var(--space-3)' }}
                >
                  <i className="fe fe-plus" aria-hidden="true" style={{ marginRight: 6 }} />
                  Add New Device
                </button>
              ) : null}

              <div className="rp-drawer-items">
                {filteredAvailable.length === 0 ? (
                  <div className="rp-drawer-empty">
                    <p className="rp-drawer-empty__text">
                      {searchTerm.trim()
                        ? "No devices match your search. If it isn't in the system yet, add it here."
                        : "Can't find the device you're looking for? Add it here."}
                    </p>
                    {/* Points back at the quick-add flow rather than off to the
                        Devices page — leaving the resident was the confusion
                        this drawer exists to remove. The role-gated fallback
                        to advanced setup lives inside that flow. */}
                    {onAddNewDevice ? (
                      <button
                        type="button"
                        onClick={onAddNewDevice}
                        className="rp-btn rp-btn--primary"
                        style={{ marginTop: 'var(--space-3)' }}
                      >
                        <i className="fe fe-plus" aria-hidden="true" style={{ marginRight: 6 }} />
                        Add New Device
                      </button>
                    ) : (
                      <Link to="/community/devices" state={{ openAddDevice: true }} className="rp-drawer-empty__link">
                        Advanced device setup
                      </Link>
                    )}
                  </div>
                ) : (
                  filteredAvailable.map((device) => {
                    const isAssignedToOther = device.resident_id && device.resident_id > 0;
                    /* Campus-wide, which is the point: this list carries devices
                       assigned to other residents and unassigned ones, and either
                       can be mid-alarm. */
                    const alarming = isAlarming(device);
                    return (
                      <div
                        key={device.device_id}
                        className={`rp-drawer-item ${isAssignedToOther ? 'rp-drawer-item--assigned-other' : ''}`}
                      >
                        <div>
                          <p className="rp-drawer-item__name"><DeviceNameLink device={device} className="rp-drawer-item__link" /></p>
                          <p className="rp-drawer-item__meta ms-2">
                            {device.inovonics_model_name ? <span>{device.inovonics_model_name}</span> : null}
                            {device.inovonics_model_name && device.manufacture_uuid ? ' · ' : ''}
                            {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : null}
                          </p>
                          {device.descriptions ? (
                            <p className="rp-drawer-item__meta ms-2">{device.descriptions}</p>
                          ) : null}
                          {isAssignedToOther ? (
                            <p className="rp-drawer-item__assigned-to">
                              <svg className="rp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                              </svg>
                              Assigned to {device.resident_name}
                            </p>
                          ) : <></>}
                          {alarming ? (
                            <p className="rp-drawer-item__alarm">
                              <span className="rp-drawer-item__alarm-dot" aria-hidden="true" />
                              In alarm
                            </p>
                          ) : null}
                        </div>
                        <BlockedReasonTip message={alarming ? ALARM_BLOCKED_MSG : ''}>
                        <button
                          type="button"
                          onClick={() => handleAssign(device)}
                          className={isAssignedToOther ? 'rp-drawer-btn--reassign' : 'rp-drawer-btn--assign'}
                          disabled={busy || alarming}
                          aria-label={
                            isAssignedToOther
                              ? `Reassign ${device.device_name} from ${device.resident_name}`
                              : `Assign ${device.device_name}`
                          }
                        >
                          {isAssignedToOther ? (
                            <>
                              <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                              </svg>
                              Reassign
                            </>
                          ) : (
                            <>
                              <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                              Assign
                            </>
                          )}
                        </button>
                        </BlockedReasonTip>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
      </>
      ) : null}
    </Drawer>
  );
};

export default DeviceDrawer;
